import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./HeaderNavbar.module.css";
import MobileHeader from "./MobileHeader";
import { navItems } from "../../controller/Navdata";
import { Link, useLocation } from "react-router-dom";

const Navbar = ({ from }) => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 992);
  const [showHeader, setShowHeader] = useState(true);
  const [activeId, setActiveId] = useState("Home");
  const [activeChildPath, setActiveChildPath] = useState(null); // ✅ new
  const [openDropdownId, setOpenDropdownId] = useState(null);

useEffect(() => {
  const path = location.pathname.toLowerCase();

  if (path === "/privacy-policy" || path === "/terms-of-use") {
    setActiveId(null);
    setActiveChildPath(null);
    return;
  }

  let matchedChildPath = null;

  const matchedItem = navItems.find((item) => {
    // ✅ 1) Check children FIRST so the exact child wins
    if (item.children && item.children.length) {
      const child = item.children.find(
        (c) => c.path?.toLowerCase() === path
      );
      if (child) {
        matchedChildPath = child.path;
        return true;
      }
      // no child matched - fall through to parent rules below
    }

    // 2) Special parent rules
    if (item.id === "join-our-team") {
      return (
        path.endsWith("/join-our-team") ||
        path.startsWith("/joinourteam_view") ||
        path.endsWith("joinourteam_apply")
      );
    }

    if (item.id === "tech-blog") {
      // still match parent for blog detail pages like /blog/article-123
      return path.startsWith("/blog");
    }

    // 3) Default exact match
    return item.path?.toLowerCase() === path;
  });

  setActiveId(matchedItem ? matchedItem.id : null);
  setActiveChildPath(matchedChildPath);
}, [location.pathname]);

  const handleClick = (id, childPath = null) => {
    setActiveId(id);
    setActiveChildPath(childPath);
    // setOpenDropdownId(null);
  };

  useEffect(() => {
    let lastY = window.scrollY;

    const handleScroll = () => {
      const currentY = window.scrollY;
      const direction = currentY > lastY ? "down" : "up";

      setIsScrolled(currentY > window.innerHeight);

      if (from === "HOME") {
        if (currentY <= window.innerHeight) {
          setShowHeader(true);
        } else {
          setShowHeader(
            currentY <= 3 * window.innerHeight || direction === "up"
          );
        }
      } else {
        setShowHeader(currentY <= window.innerHeight || direction === "up");
      }

      lastY = currentY;
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth > 992);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [from]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") setOpenDropdownId(null);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const isScrolledStyle = from !== "HOME" || isScrolled;

  return isDesktop && showHeader ? (
    <nav
      className={`${styles.navbar} ${
        from === "HOME"
          ? isScrolled
            ? styles.scrolled
            : styles.transparent
          : styles.scrolled
      }`}
    >
      <div
        className="px-5"
        style={{ maxWidth: "1480px", margin: "0 auto", width: "100%" }}
      >
        <ul className="d-flex align-items-center justify-content-between list-unstyled m-0">
          <AnimatePresence>
            {(from === "HOME"
              ? navItems.sort((a, b) =>
                  isScrolled
                    ? a.afterScrollOrder - b.afterScrollOrder
                    : a.beforeScrollOrder - b.beforeScrollOrder
                )
              : navItems.sort(
                  (a, b) => a.afterScrollOrder - b.afterScrollOrder
                )
            ).map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isOpen = openDropdownId === item.id;
              const isParentActive = activeId === item.id;

              return (
                <motion.li
                  key={item.name}
                  transition={{ type: "spring", stiffness: 100, damping: 30 }}
                  className={`d-flex align-items-center text-nowrap ${
                    hasChildren ? styles.hasDropdown : ""
                  }`}
                  onMouseEnter={() =>
                    hasChildren && setOpenDropdownId(item.id)
                  }
                  onMouseLeave={() => hasChildren && setOpenDropdownId(null)}
                >
                  <Link
                    to={item.path}
                    onClick={() => handleClick(item.id)}
                    style={item.id !== "logo" ? { padding: "30px 0px" } : {}}
                    className={`${
                      item.id !== "logo" ? styles.navItemText : ""
                    } ${
                      isScrolledStyle
                        ? styles.navItemScrolled
                        : styles.navItemDefault
                    } ${isParentActive ? styles.active : ""}`}
                    aria-haspopup={hasChildren ? "true" : undefined}
                    aria-expanded={hasChildren ? isOpen : undefined}
                  >
                    {item.name === "Logo" ? (
                      <img
                        src={
                          isScrolledStyle
                            ? item.afterScrollImage
                            : item.beforeScrollImage
                        }
                        width={190}
                        className="me-2"
                        alt="Logo"
                      />
                    ) : (
                      <span className={styles.navItemInner}>
                        {item.name}
                        {hasChildren && (
                          <svg
                            className={`${styles.caret} ${
                              isOpen ? styles.caretOpen : ""
                            }`}
                            width="10"
                            height="10"
                            viewBox="0 0 10 10"
                            aria-hidden="true"
                          >
                            <path
                              d="M1 3l4 4 4-4"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </span>
                    )}
                  </Link>

                  {/* Dropdown panel */}
                  {hasChildren && (
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          className={styles.dropdown}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 8 }}
                          transition={{ duration: 0.18 }}
                          role="menu"
                        >
                          <ul className={styles.dropdownList}>
                            {item.children.map((child) => {
                              const isChildActive =
                                activeChildPath?.toLowerCase() ===
                                child.path?.toLowerCase();
                              return (
                                <li
                                  key={child.path}
                                  className={styles.dropdownItem}
                                >
                                  <Link
                                    to={child.path}
                                    onClick={() =>
                                      handleClick(item.id, child.path)
                                    }
                                    className={`${styles.dropdownLink} ${
                                      isChildActive
                                        ? styles.dropdownLinkActive
                                        : ""
                                    }`}
                                    role="menuitem"
                                    aria-current={
                                      isChildActive ? "page" : undefined
                                    }
                                  >
                                    <span className={styles.dropdownName}>
                                      {child.name}
                                    </span>
                                    {child.description && (
                                      <span
                                        className={styles.dropdownDescription}
                                      >
                                        {child.description}
                                      </span>
                                    )}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      </div>
    </nav>
  ) : (
    <MobileHeader />
  );
};

export default Navbar;